import { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ListChecks, LayoutDashboard, Monitor, LogOut, Menu, ClipboardList, ClipboardCheck, Users, Settings, Package, Headphones, Building2, ChevronUp, Globe, BookOpen, Bot, Activity, HeartPulse } from 'lucide-react'
import { APP_VERSION } from '../../version'
import { api } from '../../utils/api'

const NAV = [
  { to: '/dashboard',  key: 'dashboard',  label: 'Dashboard',         icon: LayoutDashboard },
  { to: '/tickets',    key: 'tickets',    label: 'ServiceDesk',        icon: Headphones },
  { to: '/assets',     key: 'assets',     label: 'Assets',             icon: Monitor },
  { to: '/software',   key: 'software',   label: 'Software & License', icon: Package },
  { to: '/deliveries', key: 'deliveries', label: 'ใบส่งมอบ',            icon: ClipboardList },
  { to: '/workorders', key: 'workorders', label: 'ใบงาน',              icon: ClipboardCheck },
  { to: '/dailycheck', key: 'dailycheck', label: 'System Check',       icon: ListChecks },
  { to: '/knowledge',  key: 'knowledge',  label: 'Knowledge Base',      icon: BookOpen },
  { to: '/chatbot',    key: 'chatbot',    label: 'IT Assistant',         icon: Bot },
  { to: '/zabbix',     key: 'zabbix',     label: 'Monitor AI',          icon: Activity },
]

const ADMIN_NAV = [
  { to: '/users',    label: 'ผู้ใช้งาน', icon: Users },
  { to: '/settings', label: 'ตั้งค่าระบบ', icon: Settings },
  { to: '/status',   label: 'System Status', icon: HeartPulse },
]

interface Company { id: string; name: string; code?: string | null }
interface Props {
  user: { username: string; role: string; displayName?: string } | null
  onLogout: () => void
  children: React.ReactNode
  isSuperAdmin?: boolean
  activeCompanyId?: string | null
  onSwitchCompany?: (id: string | null) => void
  allowedMenus?: string[] | null
  onMenuAccessChanged?: () => void
}

export default function Layout({ user, onLogout, children, isSuperAdmin, activeCompanyId, onSwitchCompany, allowedMenus }: Props) {
  const location  = useLocation()
  const navigate  = useNavigate()
  const [mobileOpen, setMobileOpen]   = useState(false)
  const [companyOpen, setCompanyOpen] = useState(false)

  const isAdmin = user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN'
  const handleLogout = () => { onLogout(); navigate('/login') }
  const visibleNav = isAdmin || allowedMenus === null
    ? NAV
    : NAV.filter(n => (allowedMenus ?? []).includes(n.key))

  const { data: companies = [] } = useQuery<Company[]>({
    queryKey: ['masterdata', 'companies'],
    queryFn: () => api.get('/masterdata/companies').then(r => r.data),
    enabled: !!user,
  })

  // For regular users: their company (API returns 1-item array)
  const myCompany     = !isSuperAdmin ? companies[0] ?? null : null
  // For SUPER_ADMIN: the currently selected company
  const activeCompany = isSuperAdmin ? companies.find(c => c.id === activeCompanyId) ?? null : null

  // The company to display in the label (works for both SA and regular users)
  const displayCompany = isSuperAdmin ? activeCompany : myCompany

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="p-4 border-b border-white/10 flex-shrink-0">
        <div className="flex items-baseline gap-2">
          <h1 className="text-lg font-bold text-white">IT Management</h1>
          <span className="text-xs text-white/40 font-mono">v{APP_VERSION}</span>
        </div>
        <p className="text-xs text-white/50 mt-0.5">Management System</p>
      </div>

      {/* Nav */}
      <nav className="flex-1 min-h-0 p-2.5 space-y-0.5 overflow-y-auto" onClick={() => setCompanyOpen(false)}>
        {visibleNav.map(({ to, label, icon: Icon }) => (
          <Link key={to} to={to} onClick={() => setMobileOpen(false)}
            className={`flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm font-medium transition-all ${location.pathname.startsWith(to) ? 'bg-white/15 text-white' : 'text-white/70 hover:bg-white/10 hover:text-white'}`}>
            <Icon size={16}/>{label}
          </Link>
        ))}

        {isAdmin && (
          <>
            <div className="pt-2 pb-0.5 px-3">
              <p className="text-xs font-semibold text-white/30 uppercase tracking-wider">Admin</p>
            </div>
            {ADMIN_NAV.map(({ to, label, icon: Icon }) => (
              <Link key={to} to={to} onClick={() => setMobileOpen(false)}
                className={`flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm font-medium transition-all ${location.pathname.startsWith(to) ? 'bg-white/15 text-white' : 'text-white/70 hover:bg-white/10 hover:text-white'}`}>
                <Icon size={16}/>{label}
              </Link>
            ))}
          </>
        )}
      </nav>

      {/* User + Company section */}
      <div className="p-3 border-t border-white/10 relative flex-shrink-0">
        {/* Company row — same spot for everyone */}
        {isSuperAdmin ? (
          /* SUPER_ADMIN: clickable company switcher */
          <div className="mb-2">
            <button
              onClick={() => setCompanyOpen(v => !v)}
              className="w-full flex items-center gap-2 px-3 py-2 bg-white/10 hover:bg-white/15 rounded-xl text-xs text-white transition-colors"
            >
              {activeCompany
                ? <Building2 size={13} className="text-blue-300 flex-shrink-0"/>
                : <Globe      size={13} className="text-white/50 flex-shrink-0"/>}
              <span className="flex-1 text-left truncate">
                {activeCompany ? activeCompany.name : 'ทุกบริษัท'}
              </span>
              {activeCompany?.code && (
                <span className="text-white/30 font-mono text-xs">{activeCompany.code}</span>
              )}
              <ChevronUp size={13} className={`text-white/40 flex-shrink-0 transition-transform ${companyOpen ? 'rotate-180' : ''}`}/>
            </button>

            {/* Dropdown — opens upward */}
            {companyOpen && (
              <div className="absolute left-4 right-4 bottom-full mb-2 bg-[#1e293b] border border-white/10 rounded-xl shadow-2xl z-50 overflow-hidden">
                <div className="p-2 max-h-60 overflow-y-auto">
                  <p className="text-xs text-white/40 px-2 mb-1 font-semibold uppercase tracking-wider">เลือกบริษัท</p>
                  <button
                    onClick={() => { onSwitchCompany?.(null); setCompanyOpen(false) }}
                    className={`w-full flex items-center gap-2 px-2 py-2 rounded-lg text-xs transition-colors ${!activeCompanyId ? 'bg-blue-500/20 text-blue-300' : 'text-white/70 hover:bg-white/10 hover:text-white'}`}
                  >
                    <Globe size={12}/> ทุกบริษัท
                  </button>
                  {companies.map(c => (
                    <button
                      key={c.id}
                      onClick={() => { onSwitchCompany?.(c.id); setCompanyOpen(false) }}
                      className={`w-full flex items-center gap-2 px-2 py-2 rounded-lg text-xs text-left transition-colors ${activeCompanyId === c.id ? 'bg-blue-500/20 text-blue-300' : 'text-white/70 hover:bg-white/10 hover:text-white'}`}
                    >
                      <Building2 size={12}/> {c.name}
                      {c.code && <span className="ml-auto text-white/30 font-mono">{c.code}</span>}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : myCompany ? (
          /* Regular user: static company label */
          <div className="flex items-center gap-2 px-3 py-1.5 mb-2 bg-white/5 rounded-xl">
            <Building2 size={13} className="text-blue-300 flex-shrink-0"/>
            <span className="text-xs text-blue-200 truncate">{myCompany.name}</span>
          </div>
        ) : null}

        {/* User info row */}
        <div className="flex items-center gap-2 mb-2">
          <div className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
            {user?.username[0].toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">{user?.displayName || user?.username}</p>
            <p className="text-xs text-white/50">{user?.role}</p>
          </div>
          {isSuperAdmin && (
            <span className="text-xs bg-purple-500 text-white px-1.5 py-0.5 rounded-full flex-shrink-0">SA</span>
          )}
        </div>

        <button onClick={handleLogout}
          className="w-full flex items-center gap-2 px-3 py-1.5 text-white/70 hover:text-white hover:bg-white/10 rounded-xl text-sm transition-colors">
          <LogOut size={16}/> ออกจากระบบ
        </button>
      </div>
    </div>
  )

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      {/* Desktop sidebar */}
      <div className="hidden md:flex w-56 bg-[#0f172a] flex-col flex-shrink-0 h-full"><SidebarContent/></div>
      {/* Mobile overlay */}
      {mobileOpen && <div className="fixed inset-0 bg-black/60 z-40 md:hidden" onClick={() => setMobileOpen(false)}/>}
      {/* Mobile sidebar */}
      <div className={`fixed left-0 top-0 h-full w-56 bg-[#0f172a] z-50 transform transition-transform md:hidden ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`}><SidebarContent/></div>
      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <header className="md:hidden bg-white border-b border-gray-100 px-4 py-3 flex items-center gap-3 flex-shrink-0">
          <button onClick={() => setMobileOpen(true)} className="p-1.5 hover:bg-gray-100 rounded-lg"><Menu size={20}/></button>
          <span className="font-bold text-gray-900">IT Management</span>
          {displayCompany && (
            <span className="text-xs bg-blue-50 text-blue-600 border border-blue-200 px-2 py-0.5 rounded-full ml-auto flex items-center gap-1">
              <Building2 size={10}/>{displayCompany.name}
            </span>
          )}
          {isSuperAdmin && !activeCompany && (
            <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full ml-auto">ทุกบริษัท</span>
          )}
        </header>
        <main className="flex-1 min-h-0 overflow-y-auto" onClick={() => setCompanyOpen(false)}>{children}</main>
      </div>
    </div>
  )
}
